"use client";

import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { deliveryWarning } from "@/lib/notifications/recipients";
import type { NotificationChannelType } from "@/lib/notifications/types";
import { useTranslations } from "next-intl"


/** The minimum a picker needs; both the options endpoint and the system-notifications action supply it. */
export interface SelectableChannel {
  id: string;
  type: NotificationChannelType;
  name: string;
  enabled: boolean;
  isSystem: boolean;
  deliverable: boolean;
}

interface ChannelSelectProps {
  options: SelectableChannel[];
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  /** Shown when nothing is selected — the caller says what the fallback actually is. */
  emptyLabel: string;
}

/**
 * Compact multi-select of notification channels. Empty selection is a valid state, not an
 * error: the caller decides what it falls back to.
 *
 * Channels that cannot deliver yet (a Telegram bot nobody has `/start`ed, an email channel
 * with no recipients) stay selectable — a tenant may be about to enrol a chat — but are
 * flagged, because a silently-undeliverable choice is worse than a noisy one.
 */
export function ChannelSelect({
  options,
  value,
  onChange,
  disabled,
  loading,
  emptyLabel,
}: ChannelSelectProps) {
  const tUi = useTranslations("NotificationChannels.NotificationChannelsChannelSelect")

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  const selected = options.filter((o) => value.includes(o.id));
  const label = selected.length === 0
    ? emptyLabel
    : selected.length === 1
      ? selected[0].name
      : tUi("value0Channels", { value0: String(selected.length) });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled || loading}>
        <Button variant="outline" size="sm" className="h-8 min-w-[10rem] max-w-full justify-between font-normal">
          <span key={loading ? tUi("loading") : label} className="truncate">{loading ? tUi("loading") : label}</span>
          <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {options.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground"><span key={tUi("noChannelsAvailable")}>{tUi("noChannelsAvailable")}</span></div>
        ) : (
          options.map((opt) => (
            <DropdownMenuCheckboxItem
              key={opt.id}
              checked={value.includes(opt.id)}
              onCheckedChange={() => toggle(opt.id)}
              onSelect={(e) => e.preventDefault()}
            >
              <span className="truncate">{opt.name}</span>
              <span
                className={`ml-auto pl-2 text-xs ${
                  deliveryWarning(opt) ? "text-destructive" : "text-muted-foreground"
                }`}
              >
                {deliveryWarning(opt) ?? (opt.isSystem ? tUi("system") : opt.type)}
              </span>
            </DropdownMenuCheckboxItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
